import {
  getAllRecords,
  getRecord,
  getRecords,
} from "@/utils/helpers/data";
import { TABLES } from "@/utils/constants";

export const COLUMNS = {
  ID: "id",
  NAME: "name",
  DENTIST_ID: "dentist_id",
};

export interface HmoProvider {
  id?: string;
  name: string;
  dentist_id?: string | null;
}

/**
 * Retrieves a single HMO provider by its ID.
 * @param hmoProviderId - The ID of the HMO provider to retrieve.
 * @returns The requested HMO provider or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getHmoProvider = async (
  hmoProviderId: string | null | undefined,
): Promise<HmoProvider | null> => {
  if (!hmoProviderId) return null;
  return getRecord<HmoProvider>(
    TABLES.HMO_PROVIDERS,
    COLUMNS.ID,
    hmoProviderId,
  );
};

/**
 * Retrieves all HMO providers from the database.
 * @returns An array of all HMO providers.
 * @throws Will throw an error if the retrieval fails.
 */
export const getAllHmoProviders = async (): Promise<HmoProvider[]> => {
  return getAllRecords<HmoProvider>(TABLES.HMO_PROVIDERS);
};

/**
 * Retrieves all HMO providers for a specific dentist by the dentist's ID.
 * @param dentistId - The ID of the dentist whose HMO providers to retrieve.
 * @returns An array of HMO providers for the specified dentist.
 * @throws Will throw an error if the retrieval fails.
 */
export const getHmoProvidersByDentistId = async (
  dentistId: string,
): Promise<HmoProvider[]> => {
  return getRecords<HmoProvider>(
    TABLES.HMO_PROVIDERS,
    COLUMNS.DENTIST_ID,
    dentistId,
  );
};

/**
 * Retrieves all HMO provider options from the hmo_provider_options table.
 * @returns An array of HMO provider option names.
 * @throws Will throw an error if the retrieval fails.
 */
export const getHmoProviderOptions = async (): Promise<string[]> => {
  const hmoProviderOptions = await getAllRecords<{ name: string }>(
    TABLES.HMO_PROVIDER_OPTIONS,
  );
  return hmoProviderOptions.map((option) => option.name);
};
